import type { TreatmentType, Phase, PhaseRatios } from '../types';
import { PHASE_RATIOS, PHASES_IN_ORDER, DEFAULT_DURATION_MINUTES } from './constants';

export interface CurvePoint {
  x: number; // 0-1 normalized position
  y: number; // 0-1 intensity
  phase: Phase;
  minutes: number; // time in minutes (based on session duration)
}

export interface PhaseSegment {
  phase: Phase;
  startX: number;
  endX: number;
}

// Curve shape parameters by treatment type
// riseEnd: when the rise phase ends (0-1)
// peakStart: when peak plateau begins (0-1)
// peakEnd: when peak plateau ends and decay begins (0-1)
// riseSteepness: how sharp the rise is (higher = steeper)
// decaySteepness: how sharp the decay is (higher = steeper)
const CURVE_PARAMS: Record<TreatmentType, {
  riseEnd: number;
  peakStart: number;
  peakEnd: number;
  riseSteepness: number;
  decaySteepness: number;
}> = {
  iv: { riseEnd: 0.15, peakStart: 0.20, peakEnd: 0.45, riseSteepness: 4, decaySteepness: 2.5 },
  im: { riseEnd: 0.25, peakStart: 0.30, peakEnd: 0.50, riseSteepness: 3, decaySteepness: 2.0 },
  oral: { riseEnd: 0.35, peakStart: 0.40, peakEnd: 0.55, riseSteepness: 2, decaySteepness: 1.5 },
  spravato: { riseEnd: 0.25, peakStart: 0.30, peakEnd: 0.50, riseSteepness: 3, decaySteepness: 2.0 },
};

// Quintic smoothstep for extra-smooth transitions
function smootherstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/**
 * Get phase boundaries as x positions (0-1)
 */
export function getPhaseSegments(
  treatmentType: TreatmentType,
  phaseRatios?: PhaseRatios
): PhaseSegment[] {
  const baseRatios = phaseRatios ?? PHASE_RATIOS[treatmentType];
  const totalRatio = PHASES_IN_ORDER.reduce((sum, phase) => sum + baseRatios[phase], 0);
  const safeTotal = totalRatio > 0 ? totalRatio : 1;
  const ratios = PHASES_IN_ORDER.reduce((acc, phase) => {
    acc[phase] = baseRatios[phase] / safeTotal;
    return acc;
  }, {} as PhaseRatios);
  const segments: PhaseSegment[] = [];
  let currentX = 0;

  for (const phase of PHASES_IN_ORDER) {
    const width = ratios[phase];
    segments.push({
      phase,
      startX: currentX,
      endX: currentX + width,
    });
    currentX += width;
  }

  return segments;
}

/**
 * Get the phase at a given x position
 */
function getPhaseAtX(segments: PhaseSegment[], x: number): Phase {
  for (const segment of segments) {
    if (x >= segment.startX && x < segment.endX) {
      return segment.phase;
    }
  }
  return 'closing'; // Default to closing for x >= 1
}

/**
 * Calculate intensity using smoothstep transitions
 * Creates a smooth rise -> plateau -> decay curve
 */
function calculateIntensity(
  x: number,
  params: {
    riseEnd: number;
    peakStart: number;
    peakEnd: number;
    riseSteepness: number;
    decaySteepness: number;
  }
): number {
  const { riseEnd, peakStart, peakEnd, decaySteepness } = params;

  const minIntensity = 0.05;
  const maxIntensity = 0.98;
  const endIntensity = 0.08;

  // Rise phase: 0 to riseEnd (gentle start), riseEnd to peakStart (steeper)
  if (x <= peakStart) {
    // Two-stage rise for more natural onset
    const gentleRiseEnd = riseEnd * 0.4;
    const gentleRisePeak = 0.25; // Rise to 25% intensity gently

    if (x <= gentleRiseEnd) {
      // Very gentle initial rise
      const rise = smootherstep(0, gentleRiseEnd, x);
      return minIntensity + rise * (gentleRisePeak - minIntensity);
    } else {
      // Steeper rise to peak
      const rise = smootherstep(gentleRiseEnd, peakStart, x);
      return gentleRisePeak + rise * (maxIntensity - gentleRisePeak);
    }
  }

  // Peak plateau phase
  if (x <= peakEnd) {
    // Slight variation during peak (subtle dome shape)
    const peakProgress = (x - peakStart) / (peakEnd - peakStart);
    const peakVariation = Math.sin(peakProgress * Math.PI) * 0.02;
    return maxIntensity + peakVariation;
  }

  // Decay phase: smooth exponential-like decay
  const decayProgress = (x - peakEnd) / (1 - peakEnd);
  const decay = smootherstep(0, 1, decayProgress);

  // Use exponential-like decay for more natural feel
  const expDecay = Math.exp(-decaySteepness * decayProgress);
  const blendedDecay = decay * 0.3 + (1 - expDecay) * 0.7;

  return maxIntensity - blendedDecay * (maxIntensity - endIntensity);
}

/**
 * Generate curve points for a treatment type
 */
export function generateCurvePoints(
  treatmentType: TreatmentType,
  resolution: number = 100,
  durationMinutes: number = DEFAULT_DURATION_MINUTES,
  phaseRatios?: PhaseRatios
): CurvePoint[] {
  const segments = getPhaseSegments(treatmentType, phaseRatios);
  const params = CURVE_PARAMS[treatmentType];
  const points: CurvePoint[] = [];

  for (let i = 0; i <= resolution; i++) {
    const x = i / resolution;
    const y = calculateIntensity(x, params);
    const phase = getPhaseAtX(segments, x);
    const minutes = x * durationMinutes;

    points.push({ x, y, phase, minutes });
  }

  return points;
}

/**
 * Interpolate curve data at a specific x position
 */
export function getDataAtX(points: CurvePoint[], x: number): CurvePoint | null {
  if (points.length < 2 || x < 0 || x > 1) return null;

  // Find surrounding points
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    if (x >= p1.x && x <= p2.x) {
      // Linear interpolation
      const t = (x - p1.x) / (p2.x - p1.x);
      return {
        x,
        y: p1.y + (p2.y - p1.y) * t,
        phase: x < (p1.x + p2.x) / 2 ? p1.phase : p2.phase,
        minutes: p1.minutes + (p2.minutes - p1.minutes) * t,
      };
    }
  }

  return points[points.length - 1];
}

/**
 * Convert Catmull-Rom spline to Bezier curve for SVG path
 */
function catmullRomToBezier(
  p0: CurvePoint,
  p1: CurvePoint,
  p2: CurvePoint,
  p3: CurvePoint,
  tension: number = 0.5
): string {
  const alpha = tension;

  const cp1x = p1.x + (p2.x - p0.x) / 6 * alpha;
  const cp1y = p1.y + (p2.y - p0.y) / 6 * alpha;
  const cp2x = p2.x - (p3.x - p1.x) / 6 * alpha;
  const cp2y = p2.y - (p3.y - p1.y) / 6 * alpha;

  return `C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
}

/**
 * Generate SVG path string from curve points using Catmull-Rom interpolation
 */
export function generateSVGPath(
  points: CurvePoint[],
  width: number,
  height: number,
  padding: { top: number; bottom: number; left: number; right: number }
): string {
  if (points.length < 2) return '';

  const usableWidth = width - padding.left - padding.right;
  const usableHeight = height - padding.top - padding.bottom;

  // Transform points to SVG coordinates
  const svgPoints = points.map(p => ({
    ...p,
    x: padding.left + p.x * usableWidth,
    y: padding.top + (1 - p.y) * usableHeight, // Flip Y axis
  }));

  // Start path
  let path = `M ${svgPoints[0].x},${svgPoints[0].y}`;

  // Generate smooth curve using Catmull-Rom to Bezier conversion
  for (let i = 0; i < svgPoints.length - 1; i++) {
    const p0 = svgPoints[Math.max(0, i - 1)];
    const p1 = svgPoints[i];
    const p2 = svgPoints[i + 1];
    const p3 = svgPoints[Math.min(svgPoints.length - 1, i + 2)];

    path += ' ' + catmullRomToBezier(p0, p1, p2, p3);
  }

  return path;
}

/**
 * Generate SVG path for the filled area under the curve
 */
export function generateFilledPath(
  points: CurvePoint[],
  width: number,
  height: number,
  padding: { top: number; bottom: number; left: number; right: number }
): string {
  const curvePath = generateSVGPath(points, width, height, padding);
  if (!curvePath) return '';

  const usableWidth = width - padding.left - padding.right;
  const bottomY = height - padding.bottom;
  const startX = padding.left;
  const endX = padding.left + usableWidth;

  // Close the path along the bottom
  return `${curvePath} L ${endX},${bottomY} L ${startX},${bottomY} Z`;
}
